import { and, condition } from '@ofmos/shared/filter-ast';
import type { RecordGroupQuery } from '@ofmos/shared/record-group';
import type { RecordQuery } from '@ofmos/shared/record-query';
import type {
  BlockedField,
  EraseAllPreflight,
  EraseAllResult,
  ErasePreflight,
  EraseResult,
  FieldValue,
  GetRecordOptions,
  JunctionLinkInput,
  JunctionLinkResult,
  RecordClient,
  RecordEventPage,
  RecordGroupOptions,
  RecordGroupSet,
  RecordPage,
  RecordQueryOptions,
  RecordRow,
  SearchHit,
  WriteResult,
} from '../client';
import { toRecordEvent, toRecordRow, toRelationLabels, toValues } from './mapping';
import type { HttpMetadataClient } from './metadataClient';
import { ApiError, type Transport } from './transport';
import type {
  WireGroupResult,
  WireJunctionLinkResult,
  WirePendingWriteResult,
  WireQueryResult,
  WireRecord,
  WireRecordEventPage,
  WireSearchHit,
  WireWriteResult,
} from './wire';

/**
 * `RecordClient` over the WP3 record surface.
 *
 * `query` sends the shared `RecordQuery` VERBATIM - filter AST, cursor and all - because
 * that contract is the whole point of the RPC (INV-6, INV-7). What it adds is the system
 * field ids: `POST /api/query` returns only what `select` asks for, while a `RecordRow`
 * carries `createdAt`, `updatedBy` and `provenance` because every surface in the app shows
 * them (the timeline, the meta card, the human-lock marker). System fields are ordinary
 * field rows on the server, so asking for them is one `select` entry each rather than a
 * second endpoint.
 *
 * The three write outcomes are reconstructed from the write response:
 *  - a body carrying `pendingChange` and no record (HTTP 202) is INV-10's
 *    `pending_approval`: the call succeeded and the write did NOT happen. Only an
 *    agent-flagged credential produces it, so a human's own edit never lands here.
 *  - `blocked` non-empty is INV-5's `write_blocked`.
 *  - everything else is `applied`.
 *
 * The shape decides, not the status code: `transport.request` hands up a parsed body, and
 * keying on 202 would mean threading the status through every call site to distinguish
 * two successes.
 */

/** System fields whose values a `RecordRow` needs, by api name (`system-fields.ts`). */
export const SYSTEM_VALUE_FIELDS: ReadonlySet<string> = new Set([
  'created_at',
  'updated_at',
  'created_by',
  'updated_by',
  'provenance',
  /** D21: what makes a row in the archived segment renderable as a tombstone. */
  'deleted_at',
]);

interface SystemFieldIds {
  readonly byName: ReadonlyMap<string, string>;
  readonly ids: readonly string[];
}

export class HttpRecordClient implements RecordClient {
  constructor(
    private readonly transport: Transport,
    private readonly metadata: HttpMetadataClient,
  ) {}

  /**
   * Ids of the system fields, resolved from the metadata graph the metadata client
   * already holds. Their NAMES are fixed (`system-fields.ts`); their ids are per instance.
   */
  private async systemFields(objectId: string): Promise<SystemFieldIds> {
    const fields = await this.metadata.wireFieldsOf(objectId);
    const byName = new Map<string, string>();
    for (const field of fields) {
      if (field.isSystem && SYSTEM_VALUE_FIELDS.has(field.name)) {
        byName.set(field.name, field.id);
      }
    }
    return { byName, ids: [...byName.values()] };
  }

  /**
   * THE ARCHIVED SEGMENT'S HALF OF `archivedOnly` (admin-area.md D21).
   *
   * Two things have to be true at once, and the wire flag is only the first: `includeDeleted`
   * stops the server hiding tombstones, and a condition on `deleted_at` drops the live rows
   * that flag just let back in. Without the condition the segment would page through mostly
   * live records and look empty; without the flag the condition would match nothing.
   *
   * It is built HERE because this is the only layer that can. `deleted_at` is a system field,
   * `isDisplayableField` drops those at the metadata boundary, and no screen ever holds its
   * id - while this client already resolves system field ids to build the `select`. The node
   * itself is the ordinary shared AST (INV-6): one filter language, one evaluator, and the
   * server compiles this condition with the same compiler it uses for "stage is at risk".
   *
   * ANDed rather than replacing, so the segment composes with whatever the user filtered to.
   */
  private async archivedFilter(
    objectId: string,
    existing: RecordQuery['filter'],
  ): Promise<RecordQuery['filter']> {
    const system = await this.systemFields(objectId);
    const deletedAtId = system.byName.get('deleted_at');
    // No `deleted_at` means an instance whose metadata predates it. Widening to every row
    // would show live records under an "Archived" heading, so the segment stays empty.
    if (deletedAtId === undefined) return existing;
    const isArchived = condition(deletedAtId, 'isNotEmpty');
    return existing === undefined ? isArchived : and(existing, isArchived);
  }

  async query(query: RecordQuery, options: RecordQueryOptions = {}): Promise<RecordPage> {
    const system = await this.systemFields(query.objectId);
    const select = [...new Set([...query.select, ...system.ids])];
    const filter =
      options.archivedOnly === true
        ? await this.archivedFilter(query.objectId, query.filter)
        : query.filter;

    const result = await this.transport.request<WireQueryResult>({
      method: 'POST',
      path: '/api/query',
      body: {
        ...query,
        select,
        ...(filter === undefined ? {} : { filter }),
        ...(options.archivedOnly === true ? { includeDeleted: true } : {}),
        /**
         * D20. The server has answered this since WP3 and the browser dropped it on
         * the floor - `withTotalCount` had zero hits in `packages/web/src`. A related
         * section's "Calls (11)" is the first surface that needs it.
         */
        ...(options.withTotalCount === true ? { withTotalCount: true } : {}),
      },
    });

    return {
      records: this.toRows(result.records, system),
      ...(result.nextCursor === null ? {} : { nextCursor: result.nextCursor }),
      ...(result.totalCount === undefined ? {} : { totalCount: result.totalCount }),
    };
  }

  /**
   * THE GROUPED READ (group-by-aggregation.md D21): `POST /api/group`, sent the way
   * `query` sends its own contract - the shared `RecordGroupQuery` verbatim, with the
   * system field ids folded into the EMBEDDED query's `select`.
   *
   * They are added for the reason they are added above: a `RecordRow` carries `createdAt`,
   * `updatedBy`, `provenance` and `deletedAt` whatever produced it, and the rows seeded
   * into a group are ordinary rows. On an aggregates-only call (`rowsPerGroup: 0`) the
   * grouped statement projects none of them - the select is still not inert there, it is
   * what proves the caller may READ the fields it is grouping over (D1).
   *
   * `archivedOnly` REACHES THE EMBEDDED QUERY, not the envelope, and it is the same two
   * halves `query` already pairs (D9): `includeDeleted` stops the server hiding tombstones
   * and the `deleted_at` condition drops the live rows that flag just let back in. Both
   * land INSIDE `RecordGroupQuery.query`, which is the whole point of D1's composition -
   * the segment narrows the same `RecordQuery` object the ungrouped table sends, so a
   * grouped archive is the same population split into groups rather than a second query
   * shape with its own rules.
   */
  async queryGroups(
    query: RecordGroupQuery,
    options: RecordGroupOptions = {},
  ): Promise<RecordGroupSet> {
    const system = await this.systemFields(query.query.objectId);
    const select = [...new Set([...query.query.select, ...system.ids])];
    const archived = options.archivedOnly === true;
    const filter = archived
      ? await this.archivedFilter(query.query.objectId, query.query.filter)
      : query.query.filter;

    const result = await this.transport.request<WireGroupResult>({
      method: 'POST',
      path: '/api/group',
      body: {
        ...query,
        query: {
          ...query.query,
          select,
          ...(filter === undefined ? {} : { filter }),
          ...(archived ? { includeDeleted: true } : {}),
        },
      },
    });

    return {
      groups: result.groups.map((group) => ({
        key: group.key,
        labels: group.labels,
        count: group.count,
        aggregates: group.aggregates,
        records: this.toRows(group.records, system),
      })),
      hasMoreGroups: result.hasMoreGroups,
      total: result.total,
      ...(result.currencyCodes === undefined ? {} : { currencyCodes: result.currencyCodes }),
    };
  }

  /**
   * Wire records to `RecordRow`s, in ONE place, because a page and a group return the same
   * rows and a second copy of this is how the two would start disagreeing about which
   * fields are system ones.
   */
  private toRows(
    records: readonly { readonly id: string; readonly values: Record<string, unknown> }[],
    system: SystemFieldIds,
  ): readonly RecordRow[] {
    const skip = new Set(system.ids);
    return records.map((record) => {
      // D3: kept before `toValues` unwraps `{id, label}` down to the bare id.
      const relationLabels = toRelationLabels(record.values, skip);
      return {
        id: record.id,
        values: toValues(record.values, skip),
        ...systemValuesOf(record.values, system),
        ...(relationLabels === undefined ? {} : { relationLabels }),
      };
    });
  }

  /**
   * D5's link route. 201 for a new membership, 200 for a restored one; the body says
   * it again in `restored`, and this client reads the BODY rather than the status,
   * matching how the write outcomes are already reconstructed above.
   */
  async linkJunction(input: JunctionLinkInput): Promise<JunctionLinkResult> {
    const result = await this.transport.request<WireJunctionLinkResult>({
      method: 'POST',
      path: `/api/junctions/${input.relationFieldId}/links`,
      body: {
        sourceId: input.sourceId,
        targetId: input.targetId,
        ...(input.values === undefined ? {} : { values: input.values }),
      },
    });
    return {
      record: toRecordRow(result.record),
      junctionObjectId: result.junctionObjectId,
      restored: result.restored,
    };
  }

  /** A record that is not there is `null`, not an error: the contract says so, and a deep
   * link to a deleted record is a normal thing to happen. Every other refusal propagates. */
  async getRecord(
    objectId: string,
    recordId: string,
    options: GetRecordOptions = {},
  ): Promise<RecordRow | null> {
    try {
      const response = await this.transport.request<{ record: WireRecord }>({
        method: 'GET',
        path: `/api/records/${objectId}/${recordId}`,
        ...(options.includeDeleted === true ? { query: { includeDeleted: 'true' } } : {}),
      });
      return toRecordRow(response.record);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_found') return null;
      throw error;
    }
  }

  async createRecord(
    objectId: string,
    values: Readonly<Record<string, FieldValue>>,
  ): Promise<WriteResult> {
    const result = await this.transport.request<WireWriteResult | WirePendingWriteResult>({
      method: 'POST',
      path: `/api/records/${objectId}`,
      body: { values },
    });
    return toWriteResult(result);
  }

  async updateRecord(
    objectId: string,
    recordId: string,
    values: Readonly<Record<string, FieldValue>>,
  ): Promise<WriteResult> {
    const result = await this.transport.request<WireWriteResult | WirePendingWriteResult>({
      method: 'PATCH',
      path: `/api/records/${objectId}/${recordId}`,
      body: { values },
    });
    return toWriteResult(result);
  }

  /**
   * D24's missing verb, the one `releaseProvenanceLock` had been waiting for. Session
   * only: the route rejects an API key, so there is nothing to send but the three ids.
   */
  async releaseFieldLock(
    objectId: string,
    recordId: string,
    fieldId: string,
  ): Promise<{ readonly record: RecordRow; readonly changed: boolean }> {
    const response = await this.transport.request<{
      record: WireRecord;
      changed: boolean;
    }>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/fields/${fieldId}/release-lock`,
      body: {},
    });
    return { record: toRecordRow(response.record), changed: response.changed };
  }

  /** Soft delete (the record layer's `DELETE`); erasure is an admin action elsewhere. */
  async deleteRecord(objectId: string, recordId: string): Promise<void> {
    await this.transport.request<WireWriteResult>({
      method: 'DELETE',
      path: `/api/records/${objectId}/${recordId}`,
    });
  }

  /**
   * The undo. Object access, not admin (D21).
   *
   * The merge-loser refusal is NOT special-cased here: the server answers `invalid_state`
   * with a message naming the winner, `ApiError` carries it up, and the surface that asked
   * renders it. Translating it into a client-side flag would mean this file deciding what
   * "merged" means, which is `flipDeletion`'s job and nobody else's.
   */
  async restoreRecord(objectId: string, recordId: string): Promise<void> {
    await this.transport.request<WireWriteResult>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/restore`,
    });
  }

  /**
   * The timeline, newest page first (notes-tasks-comments.md 3.3). It used to return every
   * event a record ever had; annotation events carry full note bodies in their diffs, so
   * that became a response measured in megabytes rather than rows.
   */
  async listEvents(
    objectId: string,
    recordId: string,
    options: { readonly limit?: number; readonly cursor?: string } = {},
  ): Promise<RecordEventPage> {
    const response = await this.transport.request<WireRecordEventPage>({
      method: 'GET',
      path: `/api/records/${objectId}/${recordId}/events`,
      query: {
        ...(options.limit === undefined ? {} : { limit: options.limit }),
        ...(options.cursor === undefined ? {} : { cursor: options.cursor }),
      },
    });
    return { events: response.events.map(toRecordEvent), nextCursor: response.nextCursor };
  }

  /**
   * THE ERASURE PAIRS (accounts F2), sent as they come back: the server's own shapes, with
   * no client-side reshaping at all.
   *
   * Deliberate, and not laziness: every number in a preflight is a consequence somebody is
   * about to confirm, and a client that summed, renamed or rounded one would be the place a
   * confirmation and what it destroys drift apart. The refusals are the same - a 409
   * `confirmation_required` or `confirmation_mismatch` arrives as `ApiError` and the surface
   * that asked renders it.
   */
  async erasePreflight(objectId: string, recordId: string): Promise<ErasePreflight> {
    return this.transport.request<ErasePreflight>({
      method: 'GET',
      path: `/api/records/${objectId}/${recordId}/erase-preflight`,
    });
  }

  async erase(objectId: string, recordId: string, confirmToken: string): Promise<EraseResult> {
    return this.transport.request<EraseResult>({
      method: 'POST',
      path: `/api/records/${objectId}/${recordId}/erase`,
      body: { confirmToken },
    });
  }

  async eraseAllPreflight(objectId: string): Promise<EraseAllPreflight> {
    return this.transport.request<EraseAllPreflight>({
      method: 'GET',
      path: `/api/records/${objectId}/erase-preflight`,
    });
  }

  /**
   * The count travels beside the token because the SENTENCE the operator read carried it:
   * the token proves which records were listed, and this proves which number was shown.
   */
  async eraseAll(
    objectId: string,
    confirmToken: string,
    expectedRecordCount: number,
  ): Promise<EraseAllResult> {
    return this.transport.request<EraseAllResult>({
      method: 'POST',
      path: `/api/records/${objectId}/erase`,
      body: { confirmToken, expectedRecordCount },
    });
  }

  /**
   * The command menu's search. The server returns object ids; the secondary line is the
   * object's own label, which the metadata graph already has - so a hit reads
   * "Mila Vance / Customer" without a request per hit.
   */
  async search(term: string, limit?: number): Promise<readonly SearchHit[]> {
    const response = await this.transport.request<{ hits: readonly WireSearchHit[] }>({
      method: 'GET',
      path: '/api/search',
      query: { q: term, ...(limit === undefined ? {} : { limit }) },
    });
    const objects = await this.metadata.listObjects();
    return response.hits.map((hit) => {
      const object = objects.find((candidate) => candidate.id === hit.objectId);
      return {
        objectId: hit.objectId,
        recordId: hit.recordId,
        label: hit.label,
        ...(object === undefined ? {} : { secondary: object.labelSingular }),
      };
    });
  }
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asActorValue(value: unknown): RecordRow['createdBy'] {
  if (typeof value !== 'object' || value === null) return { source: 'SYSTEM' };
  return value as RecordRow['createdBy'];
}

/**
 * The system half of a `RecordRow`, read out of the values map a query returned.
 *
 * Missing entries (a field the caller may not read, an instance whose metadata is a poll
 * behind) become empty strings and a SYSTEM actor rather than throwing: a list that
 * refuses to render because it could not resolve `updated_by` would be worse than one
 * that shows one line less.
 */
function systemValuesOf(
  values: Readonly<Record<string, unknown>>,
  system: SystemFieldIds,
): Omit<RecordRow, 'id' | 'values'> {
  const read = (name: string): unknown => {
    const fieldId = system.byName.get(name);
    return fieldId === undefined ? undefined : values[fieldId];
  };
  const provenance = read('provenance');
  return {
    createdAt: asString(read('created_at')),
    updatedAt: asString(read('updated_at')),
    createdBy: asActorValue(read('created_by')),
    updatedBy: asActorValue(read('updated_by')),
    provenance:
      typeof provenance === 'object' && provenance !== null
        ? (provenance as RecordRow['provenance'])
        : {},
    /**
     * NOT `asString`: '' and null would both read as "live", and whether there IS a
     * timestamp is the entire question this field answers. Anything non-string becomes
     * `null`, so a field the caller may not read shows as live rather than as archived -
     * the safe direction, since the other one dims every row on an instance whose
     * metadata poll is a tick behind.
     */
    deletedAt: asTimestampOrNull(read('deleted_at')),
  };
}

function asTimestampOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function toWriteResult(result: WireWriteResult | WirePendingWriteResult): WriteResult {
  if ('pendingChange' in result) {
    return { status: 'pending_approval', pendingChangeId: result.pendingChange.id };
  }
  const record = toRecordRow(result.record);
  if (result.blocked.length === 0) return { status: 'applied', record };

  const blocked: readonly BlockedField[] = result.blocked.map((entry) => ({
    fieldId: entry.fieldId,
    attempted: entry.attempted as FieldValue,
    // What the server KEPT is the value in the record it returned.
    kept: record.values[entry.fieldId] ?? null,
    heldBy: { userId: entry.lock.userId ?? '', at: entry.lock.at },
  }));
  return { status: 'write_blocked', record, blocked };
}
